import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import PageHeader from '../../components/common/PageHeader/PageHeader';
import DataTable from '../../components/common/DataTable/DataTable';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Loading from '../../components/common/Loading/Loading';
import Button from '../../components/common/Button/Button';
import Checkbox from '../../components/common/Checkbox/Checkbox';
import FormSelect from '../../components/common/FormSelect/FormSelect';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import PlanNameWithProject from '../../components/common/PlanNameWithProject/PlanNameWithProject';
import {
  ExternalSubscription,
  PaymentGatewayService,
} from '../../services/PaymentGatewayService';
import {
  FixSubscriptionService,
  InvalidSubscriptionRecord,
} from '../../services/FixSubscriptionService';
import { PAYMENT_SYSTEM_LABELS } from '../../types/payment';
import { formatCurrency, formatDate } from '../../utils';
import styles from './FixSubscriptions.module.css';

type LookupStatus = 'idle' | 'loading' | 'found' | 'not-found' | 'error';
type FixStatus = 'idle' | 'loading' | 'success' | 'error';

interface FixSubscriptionRow extends InvalidSubscriptionRecord {
  selected: boolean;
  lookupStatus: LookupStatus;
  lookupError: string | null;
  gatewaySubscriptions: ExternalSubscription[];
  selectedGatewaySubscriptionId: string | null;
  fixStatus: FixStatus;
  fixError: string | null;
}

const ACTIVE_STATUSES = new Set(['ACTIVE', 'APPROVED']);

const isValidGatewaySubscriptionId = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed.length > 0 && !/\s/.test(trimmed);
};

const getSubscriptionSortValue = (subscription: ExternalSubscription): number => {
  const date = subscription.dateCreated ? Date.parse(subscription.dateCreated) : 0;
  return Number.isFinite(date) ? date : 0;
};

const suggestGatewaySubscriptionId = (
  subscriptions: ExternalSubscription[],
  portalSubscriptionId: string,
): string | null => {
  const portalIdTokens = new Set(
    portalSubscriptionId.split(/\s+/).map((part) => part.trim()).filter(Boolean),
  );

  const gatewaySubscriptions = subscriptions.filter((item) => {
    const gatewayId = item.external_subscription_id.trim();
    return isValidGatewaySubscriptionId(gatewayId) && !portalIdTokens.has(gatewayId);
  });

  const candidates = gatewaySubscriptions.length > 0
    ? gatewaySubscriptions
    : subscriptions.filter((item) => isValidGatewaySubscriptionId(item.external_subscription_id));

  if (candidates.length === 0) {
    return null;
  }

  const active = candidates.filter((item) => ACTIVE_STATUSES.has(item.status.toUpperCase()));
  const pool = active.length > 0 ? active : candidates;

  if (pool.length === 1) {
    return pool[0].external_subscription_id.trim();
  }

  return [...pool]
    .sort((left, right) => getSubscriptionSortValue(right) - getSubscriptionSortValue(left))[0]
    ?.external_subscription_id.trim() ?? null;
};

const flattenGatewaySubscriptions = (
  groups: Awaited<ReturnType<typeof PaymentGatewayService.lookupStudentSubscriptionCandidates>>,
): ExternalSubscription[] => {
  const deduped = new Map<string, ExternalSubscription>();

  for (const group of groups) {
    for (const subscription of group.subscriptions) {
      const gatewayId = subscription.external_subscription_id.trim();
      if (!isValidGatewaySubscriptionId(gatewayId)) {
        continue;
      }

      deduped.set(gatewayId, {
        ...subscription,
        external_subscription_id: gatewayId,
      });
    }
  }

  return Array.from(deduped.values()).sort(
    (left, right) => getSubscriptionSortValue(right) - getSubscriptionSortValue(left),
  );
};

const buildSubscriptionLabel = (subscription: ExternalSubscription): string => {
  const parts = [
    subscription.external_subscription_id,
    subscription.status,
    subscription.description ?? 'Sem descrição',
    formatCurrency(subscription.value),
  ];

  if (subscription.dateCreated) {
    parts.push(formatDate(subscription.dateCreated.split('T')[0]));
  }

  return parts.join(' • ');
};

const toRow = (record: InvalidSubscriptionRecord): FixSubscriptionRow => ({
  ...record,
  selected: false,
  lookupStatus: 'idle',
  lookupError: null,
  gatewaySubscriptions: [],
  selectedGatewaySubscriptionId: null,
  fixStatus: 'idle',
  fixError: null,
});

const GRID_TEMPLATE = '52px minmax(180px, 1.1fr) 80px minmax(140px, 0.9fr) minmax(200px, 1.1fr) 120px minmax(240px, 1.4fr) 110px';

const getHeaders = (allSelected: boolean, onToggleAll: () => void): React.ReactNode[] => [
  <div key="select-all" className={styles['checkbox-cell']}>
    <Checkbox
      checked={allSelected}
      onPress={onToggleAll}
      ariaLabel="Selecionar todas"
      variant="classic"
      compact
      className={styles['checkbox-control']}
    />
  </div>,
  'Aluna',
  'Gateway',
  'Plano',
  'ID atual',
  'Status busca',
  'ID sugerido',
  'Correção',
];

const FixSubscriptions: React.FC = () => {
  const [rows, setRows] = useState<FixSubscriptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [confirmApplyOpen, setConfirmApplyOpen] = useState(false);
  const autoLookupScheduledRef = useRef(false);

  const runLookupForRows = useCallback(async (targets: FixSubscriptionRow[]) => {
    if (targets.length === 0) {
      setActionError('Selecione ao menos uma assinatura para buscar no gateway.');
      return;
    }

    setActionError(null);
    setActionSuccess(null);
    setIsLookingUp(true);

    const selectedIds = new Set(targets.map((row) => row.studentSubscriptionId));

    setRows((current) => current.map((row) => (
      selectedIds.has(row.studentSubscriptionId)
        ? {
          ...row,
          selected: true,
          lookupStatus: 'loading',
          lookupError: null,
          gatewaySubscriptions: [],
          selectedGatewaySubscriptionId: null,
          fixStatus: 'idle',
          fixError: null,
        }
        : row
    )));

    let foundCount = 0;
    let errorCount = 0;
    const patches = new Map<number, Partial<FixSubscriptionRow>>();

    try {
      for (const row of targets) {
        try {
          const groups = await PaymentGatewayService.lookupStudentSubscriptionCandidates(
            row.paymentSystem,
            row.studentSubscriptionId,
          );
          const subscriptions = flattenGatewaySubscriptions(groups);
          const suggestedId = suggestGatewaySubscriptionId(subscriptions, row.externalSubscriptionId);

          if (!suggestedId) {
            errorCount += 1;
            patches.set(row.studentSubscriptionId, {
              lookupStatus: 'not-found',
              lookupError: 'Nenhuma assinatura válida encontrada no gateway para esta aluna.',
              gatewaySubscriptions: [],
              selectedGatewaySubscriptionId: null,
            });
            continue;
          }

          foundCount += 1;
          patches.set(row.studentSubscriptionId, {
            lookupStatus: 'found',
            lookupError: null,
            gatewaySubscriptions: subscriptions,
            selectedGatewaySubscriptionId: suggestedId,
          });
        } catch (err) {
          errorCount += 1;
          patches.set(row.studentSubscriptionId, {
            lookupStatus: 'error',
            lookupError: err instanceof Error ? err.message : 'Falha ao consultar o gateway.',
            gatewaySubscriptions: [],
            selectedGatewaySubscriptionId: null,
          });
        }
      }

      setRows((current) => current.map((row) => {
        const patch = patches.get(row.studentSubscriptionId);
        return patch ? { ...row, ...patch } : row;
      }));

      setActionSuccess(`Busca concluída: ${foundCount} com assinatura encontrada, ${errorCount} com erro ou sem resultado.`);
    } finally {
      setIsLookingUp(false);
    }
  }, []);

  const loadRows = useCallback(async () => {
    autoLookupScheduledRef.current = false;
    setLoading(true);
    setLoadError(null);

    try {
      const records = await FixSubscriptionService.listInvalidExternalSubscriptionIds();
      setRows(records.map((record) => ({ ...toRow(record), selected: true })));
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Não foi possível carregar as assinaturas.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    if (loading || rows.length === 0 || autoLookupScheduledRef.current || isLookingUp) {
      return;
    }

    const pendingRows = rows.filter(
      (row) => row.lookupStatus === 'idle' && row.fixStatus !== 'success',
    );

    if (pendingRows.length === 0) {
      return;
    }

    autoLookupScheduledRef.current = true;
    void runLookupForRows(pendingRows);
  }, [loading, rows, isLookingUp, runLookupForRows]);

  const selectedRows = useMemo(
    () => rows.filter((row) => row.selected && row.fixStatus !== 'success'),
    [rows],
  );

  const rowsReadyToApply = useMemo(
    () => selectedRows.filter(
      (row) => row.selectedGatewaySubscriptionId && row.lookupStatus === 'found',
    ),
    [selectedRows],
  );

  const allSelected = rows.length > 0 && rows.every((row) => row.selected || row.fixStatus === 'success');

  const toggleSelectAll = () => {
    setRows((current) => {
      const shouldSelectAll = !allSelected;
      return current.map((row) => (
        row.fixStatus === 'success'
          ? row
          : { ...row, selected: shouldSelectAll }
      ));
    });
  };

  const toggleRowSelection = (studentSubscriptionId: number) => {
    setRows((current) => current.map((row) => (
      row.studentSubscriptionId === studentSubscriptionId
        ? { ...row, selected: !row.selected }
        : row
    )));
  };

  const updateRow = (
    studentSubscriptionId: number,
    patch: Partial<FixSubscriptionRow>,
  ) => {
    setRows((current) => current.map((row) => (
      row.studentSubscriptionId === studentSubscriptionId
        ? { ...row, ...patch }
        : row
    )));
  };

  const handleLookupSelected = () => {
    void runLookupForRows(selectedRows);
  };

  const handleLookupRow = (studentSubscriptionId: number) => {
    const row = rows.find((item) => item.studentSubscriptionId === studentSubscriptionId);
    if (!row || row.fixStatus === 'success') {
      return;
    }

    void runLookupForRows([row]);
  };

  const handleApplyFixes = async () => {
    if (rowsReadyToApply.length === 0) {
      setActionError('Selecione assinaturas com ID sugerido antes de aplicar.');
      setConfirmApplyOpen(false);
      return;
    }

    setActionError(null);
    setActionSuccess(null);
    setIsApplying(true);

    const applyIds = new Set(rowsReadyToApply.map((row) => row.studentSubscriptionId));
    setRows((current) => current.map((row) => (
      applyIds.has(row.studentSubscriptionId)
        ? { ...row, fixStatus: 'loading', fixError: null }
        : row
    )));

    let successCount = 0;
    let errorCount = 0;
    const patches = new Map<number, Partial<FixSubscriptionRow>>();

    try {
      for (const row of rowsReadyToApply) {
        if (!row.selectedGatewaySubscriptionId) {
          continue;
        }

        patches.set(row.studentSubscriptionId, {
          fixStatus: 'loading',
          fixError: null,
        });

        try {
          await PaymentGatewayService.fixExternalSubscriptionId(
            row.paymentSystem,
            row.studentSubscriptionId,
            row.selectedGatewaySubscriptionId,
          );

          successCount += 1;
          patches.set(row.studentSubscriptionId, {
            fixStatus: 'success',
            fixError: null,
            selected: false,
            externalSubscriptionId: row.selectedGatewaySubscriptionId,
          });
        } catch (err) {
          errorCount += 1;
          patches.set(row.studentSubscriptionId, {
            fixStatus: 'error',
            fixError: err instanceof Error ? err.message : 'Não foi possível aplicar a correção.',
          });
        }
      }

      setRows((current) => current.map((row) => {
        const patch = patches.get(row.studentSubscriptionId);
        return patch ? { ...row, ...patch } : row;
      }));

      setActionSuccess(`Correções aplicadas: ${successCount} sucesso, ${errorCount} erro.`);
    } finally {
      setIsApplying(false);
      setConfirmApplyOpen(false);
    }
  };

  const renderLookupBadge = (row: FixSubscriptionRow) => {
    const labels: Record<LookupStatus, string> = {
      idle: 'Pendente',
      loading: 'Buscando...',
      found: 'Encontrada',
      'not-found': 'Não encontrada',
      error: 'Erro',
    };

    return (
      <span className={`${styles.badge} ${styles[`badge-${row.lookupStatus}`]}`}>
        {labels[row.lookupStatus]}
      </span>
    );
  };

  if (loading) {
    return (
      <PageContainer className={styles.page}>
        <PageHeader title="Corrigir assinaturas" subtitle="Ferramenta interna para ajustar external_subscription_id inválidos." />
        <Loading />
      </PageContainer>
    );
  }

  return (
    <PageContainer className={styles.page}>
      <PageHeader
        title="Corrigir assinaturas"
        subtitle="Lista assinaturas com external_subscription_id inválido no portal, consulta o gateway pelo cliente vinculado e sugere o ID retornado pela API."
      />

      {loadError && (
        <div className={`${styles.alert} ${styles['alert-error']}`}>{loadError}</div>
      )}

      {actionError && (
        <div className={`${styles.alert} ${styles['alert-error']}`}>{actionError}</div>
      )}

      {actionSuccess && (
        <div className={`${styles.alert} ${styles['alert-success']}`}>{actionSuccess}</div>
      )}

      <div className={styles.toolbar}>
        <span className={styles.summary}>
          {rows.length} assinatura(s) com ID inválido • {selectedRows.length} selecionada(s)
        </span>
        <div className={styles['toolbar-actions']}>
          <Button variant="ghost" size="small" onClick={() => void loadRows()} disabled={loading || isLookingUp || isApplying}>
            Recarregar
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleLookupSelected}
            disabled={selectedRows.length === 0 || isLookingUp || isApplying}
          >
            {isLookingUp ? 'Buscando...' : 'Buscar selecionadas'}
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={() => setConfirmApplyOpen(true)}
            disabled={rowsReadyToApply.length === 0 || isLookingUp || isApplying}
          >
            Aplicar correções
          </Button>
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState message="Nenhuma assinatura com external_subscription_id inválido encontrada." icon="task_alt" />
      ) : (
        <div className={styles['table-scroll']}>
          <DataTable
            headers={getHeaders(allSelected, toggleSelectAll)}
            gridTemplate={GRID_TEMPLATE}
            borderedRows
            boldHeaders
          >
            {rows.map((row) => {
              const subscriptionOptions = row.gatewaySubscriptions.map((subscription) => ({
                value: subscription.external_subscription_id,
                label: buildSubscriptionLabel(subscription),
              }));

              return (
                <DataTable.Row
                  key={row.studentSubscriptionId}
                  highlight={row.selected}
                >
                  <div
                    className={styles['checkbox-cell']}
                    onClick={(event) => event.stopPropagation()}
                  >
                    <Checkbox
                      checked={row.selected}
                      onPress={() => toggleRowSelection(row.studentSubscriptionId)}
                      disabled={row.fixStatus === 'success' || isLookingUp || isApplying}
                      ariaLabel={`Selecionar assinatura ${row.studentSubscriptionId}`}
                      variant="classic"
                      compact
                      className={styles['checkbox-control']}
                    />
                  </div>

                  <div className={styles['student-cell']}>
                    <Link to={`/alunas/${row.studentId}/editar`} className={styles['student-link']}>
                      {row.studentName}
                    </Link>
                    <div className={styles['lookup-message']}>
                      {row.studentEmail ?? 'Sem e-mail'}
                      {row.canceledAt ? ` • Cancelada em ${formatDate(row.canceledAt.split('T')[0])}` : ''}
                    </div>
                  </div>

                  <span className={styles['text-cell']}>{PAYMENT_SYSTEM_LABELS[row.paymentSystem]}</span>

                  <span className={styles['text-cell']}>
                    <PlanNameWithProject
                      name={row.planName || row.planDescription}
                      project={row.project}
                    />
                  </span>

                  <span className={`${styles['text-cell']} ${row.fixStatus === 'success' ? styles['fixed-id'] : styles['invalid-id']}`}>
                    {row.externalSubscriptionId}
                  </span>

                  <div className={styles['lookup-status-cell']}>
                    {renderLookupBadge(row)}
                    {row.lookupStatus === 'idle' && row.fixStatus !== 'success' && (
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => handleLookupRow(row.studentSubscriptionId)}
                        disabled={isLookingUp || isApplying}
                      >
                        Buscar
                      </Button>
                    )}
                  </div>

                  <div className={styles['lookup-cell']}>
                    {row.lookupStatus === 'found' && subscriptionOptions.length > 0 ? (
                      <FormSelect
                        options={subscriptionOptions}
                        value={row.selectedGatewaySubscriptionId ?? ''}
                        onChange={(event) => updateRow(row.studentSubscriptionId, {
                          selectedGatewaySubscriptionId: event.target.value || null,
                        })}
                        containerClassName={styles['subscription-select']}
                        disabled={row.fixStatus === 'success' || isApplying}
                      />
                    ) : (
                      <span className={`${styles['lookup-message']} ${row.lookupError ? styles['lookup-message-error'] : ''}`}>
                        {row.lookupError ?? (row.lookupStatus === 'idle' ? 'Aguardando consulta no gateway' : '—')}
                      </span>
                    )}
                  </div>

                  <div className={styles['text-cell']}>
                    {row.fixStatus === 'success' && (
                      <span className={`${styles.badge} ${styles['badge-fixed']}`}>Corrigida</span>
                    )}
                    {row.fixStatus === 'loading' && (
                      <span className={`${styles.badge} ${styles['badge-loading']}`}>Salvando...</span>
                    )}
                    {row.fixStatus === 'error' && (
                      <span className={`${styles['lookup-message']} ${styles['lookup-message-error']}`}>
                        {row.fixError}
                      </span>
                    )}
                    {row.fixStatus === 'idle' && '—'}
                  </div>
                </DataTable.Row>
              );
            })}
          </DataTable>
        </div>
      )}

      <ConfirmModal
        open={confirmApplyOpen}
        variant="warning"
        icon="build"
        title="Aplicar correções"
        message={`Deseja atualizar ${rowsReadyToApply.length} assinatura(s) com o external_subscription_id encontrado no gateway?`}
        confirmLabel={isApplying ? 'Aguarde...' : 'Aplicar'}
        cancelLabel="Cancelar"
        loading={isApplying}
        onConfirm={() => void handleApplyFixes()}
        onCancel={() => setConfirmApplyOpen(false)}
      />
    </PageContainer>
  );
};

export default FixSubscriptions;
